/**
 * 用户留存（ADM-MTR-S015）共享契约：GET /api/v1/metrics/retention-cohorts 的查询参数与响应形态。
 *
 * 按天 / 周 / 月统计 cohort 用户在起点后第 N 个周期内是否回访（dev-docs AD §18）。cohort 起点随
 * user_type 变：all / free 取激活，payer 取首次付费（历史最早一笔 Production 到账）。
 */

import { z } from 'zod';
import { metricsCalendarDay } from './metrics-paywall-conversion';

export const RETENTION_COHORT_GRANULARITIES = ['day', 'week', 'month'] as const;
export type RetentionCohortGranularity = (typeof RETENTION_COHORT_GRANULARITIES)[number];

/** 每种粒度返回的周期序号（顺序即列顺序） */
export const RETENTION_COHORT_PERIODS: Record<RetentionCohortGranularity, readonly number[]> = {
  day: Array.from({ length: 27 }, (_, index) => index + 1),
  week: [1, 2, 3, 4, 5, 6, 7, 8],
  month: [1, 2, 3, 4, 5, 6],
};

/** 每种粒度的时间窗跨度上限（天，含首尾） */
export const RETENTION_COHORT_MAX_DAYS: Record<RetentionCohortGranularity, number> = {
  day: 92,
  week: 182,
  month: 366,
};

/** all = 激活 cohort 全部；payer = 按首次付费分组；free = 激活且至今未付费 */
export const RETENTION_COHORT_USER_TYPES = ['all', 'payer', 'free'] as const;
export type RetentionCohortUserType = (typeof RETENTION_COHORT_USER_TYPES)[number];

export const RETENTION_COHORT_GROUP_DIMENSIONS = [
  'app',
  'channel',
  'platform',
  'language',
  'book_code',
  'payer_status',
  'country',
] as const;
export type RetentionCohortGroupDimension = (typeof RETENTION_COHORT_GROUP_DIMENSIONS)[number];
/** 旧版单维度兼容参数；新页面使用 group_by。 */
export const RETENTION_COHORT_SPLIT_DIMENSIONS = ['language', 'country'] as const;
export type RetentionCohortSplitDimension = (typeof RETENTION_COHORT_SPLIT_DIMENSIONS)[number];

/** 每行明细子行上限；超出时 children_truncated=true */
export const RETENTION_COHORT_MAX_CHILDREN = 100;

/** 以重复查询参数传递的多值参数名（路由按此把同名参数收成数组） */
export const RETENTION_COHORT_LIST_PARAMS = ['app_ids', 'languages', 'countries'] as const;

/**
 * 多值筛选：重复查询参数、不按逗号拆分；与付费转化看板的同名工具不同，这里保留空串——
 * 契约规定 languages / countries 传空串表示「该维度为 null 的用户」。
 */
const repeatedListKeepingEmpty = <T extends string>(item: z.ZodType<T, string>) =>
  z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((raw) => [
      ...new Set(
        (raw === undefined ? [] : Array.isArray(raw) ? raw : [raw]).map((value) => value.trim()),
      ),
    ])
    .pipe(z.array(item).max(50));

const DAY_MS = 86_400_000;

export const retentionCohortQuerySchema = z
  .object({
    granularity: z.enum(RETENTION_COHORT_GRANULARITIES).default('day'),
    from: metricsCalendarDay,
    to: metricsCalendarDay,
    app_ids: z
      .union([z.string(), z.array(z.string())])
      .optional()
      .transform((raw) => [
        ...new Set(
          (raw === undefined ? [] : Array.isArray(raw) ? raw : [raw])
            .map((value) => value.trim().toLowerCase())
            .filter(Boolean),
        ),
      ])
      .pipe(z.array(z.string().uuid()).max(50)),
    user_type: z.enum(RETENTION_COHORT_USER_TYPES).default('all'),
    languages: repeatedListKeepingEmpty(z.string().max(16)),
    countries: repeatedListKeepingEmpty(
      z.string().regex(/^([A-Z]{2})?$/, '地区须为 ISO 3166-1 alpha-2 大写或空串'),
    ),
    group_by: z
      .string()
      .optional()
      .transform((raw) =>
        raw === undefined || raw.trim() === ''
          ? ['language']
          : [
              ...new Set(
                raw
                  .split(',')
                  .map((value) => value.trim())
                  .filter(Boolean),
              ),
            ],
      )
      .transform((dims) => (dims.length ? dims : ['language']))
      .pipe(
        z
          .array(z.enum(RETENTION_COHORT_GROUP_DIMENSIONS))
          .max(RETENTION_COHORT_GROUP_DIMENSIONS.length),
      ),
    split_by: z.enum(RETENTION_COHORT_SPLIT_DIMENSIONS).optional(),
  })
  // 拒绝未声明参数：误拼的筛选静默丢弃会返回看似合理的全量数据
  .strict()
  .superRefine((query, ctx) => {
    const fromMs = Date.parse(`${query.from}T00:00:00Z`);
    const span = (Date.parse(`${query.to}T00:00:00Z`) - fromMs) / DAY_MS;
    if (span < 0) ctx.addIssue({ code: 'custom', path: ['to'], message: 'to 不得早于 from' });
    else if (span + 1 > RETENTION_COHORT_MAX_DAYS[query.granularity])
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: `时间窗最多 ${RETENTION_COHORT_MAX_DAYS[query.granularity]} 天`,
      });
    const from = new Date(fromMs);
    if (query.granularity === 'week' && from.getUTCDay() !== 1)
      ctx.addIssue({ code: 'custom', path: ['from'], message: '按周时 from 须为周一' });
    if (query.granularity === 'month' && from.getUTCDate() !== 1)
      ctx.addIssue({ code: 'custom', path: ['from'], message: '按月时 from 须为 1 号' });
  });
export type RetentionCohortQuery = z.infer<typeof retentionCohortQuerySchema>;

/** 单格成因：null = 已成熟且有数据；其余两值见 AD §18.2 格状态表 */
export type RetentionCohortCellStatus = 'PENDING_MATURITY' | 'PENDING_INSTRUMENTATION' | null;

export type RetentionCohortCell = {
  period: number;
  retained: number | null;
  cohort_size: number | null;
  status: RetentionCohortCellStatus;
};

export type RetentionCohortChild = {
  /** 单维度兼容值；null = 未知或多维度组合 */
  dim: string | null;
  /** 当前分组键和值；null = 未知 / 未上报 */
  dims: Partial<Record<RetentionCohortGroupDimension, string | null>>;
  cohort_users: number;
  cells: RetentionCohortCell[];
};

export type RetentionCohortRow = {
  cohort_start: string;
  cohort_end: string;
  /** 周 / 月 cohort 尚未结束时为 false（仍在接收新用户） */
  cohort_complete: boolean;
  cohort_users: number;
  cells: RetentionCohortCell[];
  /** 仅传 split_by 时返回 */
  children?: RetentionCohortChild[];
  children_truncated?: boolean;
};

export type RetentionCohortData = {
  window: { from: string; to: string; timezone: 'UTC' };
  granularity: RetentionCohortGranularity;
  periods: number[];
  last_complete_day: string;
  user_type: RetentionCohortUserType;
  group_by: RetentionCohortGroupDimension[];
  split_by: RetentionCohortSplitDimension | null;
  summary: { cohort_users: number; cells: RetentionCohortCell[] };
  /** cohort_start 降序（最近的在上） */
  rows: RetentionCohortRow[];
  /** 筛选候选值：同 App / 时间窗 / 用户类型的 cohort 用户中出现过的值（不受语言、地区筛选约束） */
  options: {
    apps: { id: string; name: string; platform: string | null }[];
    languages: string[];
    countries: string[];
    channels: string[];
  };
  generated_at: string;
};
